import React, { useState, useEffect } from 'react'
import { Container, Row, Col, Alert } from 'react-bootstrap'
import { GetSystemReportsUseCase } from '../../data/usecases/Reports/RemoteGetSystemReports'
import { BlockchainOverviewCard } from '../Reports/components/BlockchainOverviewCard'
import { ContractMetricsCard } from '../Reports/components/ContractMetricsCard'
import { FinancialMetricsCard } from '../Reports/components/FinancialMetricsCard'
import { TransactionTable } from '../Reports/components/TransactionTable'
import { SmartContractTable } from '../Reports/components/SmartContractTable'
import { TokenMetricsCard } from '../Reports/components/TokenMetricsCard'
import { BillingAnalyticsCard } from '../Reports/components/BillingAnalyticsCard'
import { PDFDownloadButton } from '../Reports/components/PDFDownloadButton'
import { PlatformOverviewCards } from '../Reports/components/PlatformOverviewCards'
import { NetworkInfoCard } from '../Reports/components/NetworkInfoCard'
import * as S from '../Reports/styles'
import { ReportData } from '../Reports/types'

interface SystemReportsProps {
  remoteGetSystemReports: GetSystemReportsUseCase
  secret: string
}

const SystemReports: React.FC<SystemReportsProps> = ({
  remoteGetSystemReports,
  secret
}) => {
  const [reportData, setReportData] = useState<ReportData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date())

  useEffect(() => {
    loadReportData()
  }, [secret])

  const loadReportData = async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await remoteGetSystemReports.execute(secret)
      setReportData(data)
      setLastUpdated(new Date())
    } catch (err) {
      console.error('Error loading system report data:', err)
      setError(
        'Erro ao carregar dados do relatório do sistema. Verifique a chave secreta.'
      )
    } finally {
      setLoading(false)
    }
  }

  const handleRefresh = () => {
    loadReportData()
  }

  if (error) {
    return (
      <Container fluid>
        <Alert variant="danger">
          <h4>Erro ao carregar relatório do sistema</h4>
          <p>{error}</p>
        </Alert>
      </Container>
    )
  }

  return (
    <S.ReportContainer>
      <Container fluid>
        {/* Header Section */}
        <S.ReportHeader>
          <Row>
            <Col md={8}>
              <S.ReportTitle>Relatório do Sistema PropToken</S.ReportTitle>
              <S.ReportSubtitle>
                Visão completa de todas as operações da plataforma (Nível de
                Sistema)
              </S.ReportSubtitle>
              <S.LastUpdated>
                Última atualização: {lastUpdated.toLocaleString('pt-BR')}
              </S.LastUpdated>
            </Col>
            <Col md={4} className="text-right">
              <S.ActionButtonsContainer>
                <S.RefreshButton
                  variant="outline-primary"
                  onClick={handleRefresh}
                  disabled={loading}
                >
                  <i className="feather icon-refresh-cw" /> Atualizar
                </S.RefreshButton>
                <PDFDownloadButton
                  reportData={reportData}
                  isSystemReport={true}
                  secret={secret}
                />
              </S.ActionButtonsContainer>
            </Col>
          </Row>
        </S.ReportHeader>

        {/* Platform Overview KPIs */}
        {reportData && (
          <PlatformOverviewCards
            data={reportData.platformOverview}
            loading={loading}
          />
        )}

        {/* Main Content Grid */}
        <Row className="mb-4">
          {/* First Row */}
          <Col lg={6} xl={4} className="mb-4">
            <BlockchainOverviewCard
              data={reportData?.blockchainOverview}
              loading={loading}
            />
          </Col>
          <Col lg={6} xl={4} className="mb-4">
            <ContractMetricsCard
              data={reportData?.contractMetrics}
              loading={loading}
            />
          </Col>
          <Col lg={12} xl={4} className="mb-4">
            <FinancialMetricsCard
              data={reportData?.financialMetrics}
              loading={loading}
            />
          </Col>
        </Row>

        <Row className="mb-4">
          {/* Second Row */}
          <Col lg={6} className="mb-4">
            <TokenMetricsCard
              data={reportData?.tokenMetrics}
              loading={loading}
            />
          </Col>
          <Col lg={6} className="mb-4">
            <BillingAnalyticsCard
              data={reportData?.billingAnalytics}
              loading={loading}
            />
          </Col>
        </Row>

        {/* Network Information Row */}
        <Row className="mb-4">
          <Col lg={12}>
            <NetworkInfoCard data={reportData?.networkInfo} loading={loading} />
          </Col>
        </Row>

        {/* Tables Section */}
        <Row className="mb-4">
          <Col lg={12}>
            <div className="card">
              <div className="card-header">
                <h5 className="card-title">Transações Blockchain Recentes</h5>
              </div>
              <div className="card-body">
                <TransactionTable
                  transactions={reportData?.recentTransactions || []}
                  loading={loading}
                />
              </div>
            </div>
          </Col>
        </Row>

        <Row className="mb-4">
          <Col lg={12}>
            <div className="card">
              <div className="card-header">
                <h5 className="card-title">Smart Contracts Implantados</h5>
              </div>
              <div className="card-body">
                <SmartContractTable
                  contracts={reportData?.smartContracts || []}
                  loading={loading}
                />
              </div>
            </div>
          </Col>
        </Row>

        {/* Footer */}
        <S.ReportFooter>
          <p>
            Este é o relatório de sistema completo da plataforma PropToken,
            incluindo dados de todas as empresas e usuários. Todos os hashes de
            transação são verificáveis na rede Hyperledger Besu.
          </p>
          <p>
            <strong>ACESSO RESTRITO:</strong> Este relatório contém informações
            sensíveis de todas as operações do sistema.
          </p>
        </S.ReportFooter>
      </Container>
    </S.ReportContainer>
  )
}

export default SystemReports
